import { PLATFORM_BASE_URL } from "../platform/config";
import { Badge, Box, Text } from "@chakra-ui/react";
import React from "react";
import {
  OktonErrorState,
  OktonDataGrid,
  OktonDataGridColumn,
  OktonPageHeader,
  useOktonAuth,
  formatDateTimeSeconds as formatDate,
} from "@okton/platform-ui";

interface PlatformEvent {
  id: string;
  subjectId?: string;
  applicationId?: string;
  action: string;
  context: Record<string, unknown>;
  createdAt: string;
  userName?: string;
  userEmail?: string;
}

function contextText(context: Record<string, unknown>): string {
  const entries = Object.entries(context || {});
  if (entries.length === 0) return "-";
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join(" • ");
}

export const EventsPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [events, setEvents] = React.useState<PlatformEvent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      if (!accessToken) return;
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${PLATFORM_BASE_URL}/v1/admin/events?limit=200`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: "include",
          },
        );
        const payload = await response.json();
        if (!response.ok)
          throw new Error(
            payload.message || "Não foi possível carregar os eventos.",
          );
        if (active) setEvents(payload.data || []);
      } catch (err) {
        if (active)
          setError(
            err instanceof Error ? err.message : "Erro ao carregar eventos.",
          );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [accessToken]);

  const columns = React.useMemo<OktonDataGridColumn<PlatformEvent>[]>(
    () => [
      {
        key: "date",
        header: "Data",
        render: (event) => (
          <Text whiteSpace="nowrap">{formatDate(event.createdAt)}</Text>
        ),
      },
      {
        key: "action",
        header: "Ação",
        render: (event) => <Badge colorScheme="blue">{event.action}</Badge>,
      },
      {
        key: "system",
        header: "Sistema",
        value: (event) => event.applicationId || "-",
      },
      {
        key: "user",
        header: "Usuário",
        render: (event) => (
          <Box>
            <Text fontWeight="600">
              {event.userName || event.userEmail || "Usuário"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {event.userEmail || event.subjectId || "-"}
            </Text>
          </Box>
        ),
      },
      {
        key: "context",
        header: "Contexto",
        render: (event) => (
          <Text fontSize="xs" color="gray.500" noOfLines={3} maxW="420px">
            {contextText(event.context)}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Eventos"
        subtitle="Eventos registrados pelo ecossistema Okton."
      />

      {error ? (
        <OktonErrorState
          title="Não foi possível carregar os eventos"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={events}
          columns={columns}
          getRowId={(event) => event.id}
          loading={loading}
          emptyMessage="Nenhum evento registrado. Os eventos da plataforma aparecerão aqui quando forem registrados."
          hidePagination
        />
      )}
    </Box>
  );
};
